/**
 * OS Deliverables TanStack Query Hooks
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import * as api from '../api';
import { rockKeys } from './useRocks';
import type { CreateDeliverableInput } from '../types';

// =============================================================================
// Helpers
// =============================================================================

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return 'An unexpected error occurred';
}

// =============================================================================
// Mutation Hooks
// =============================================================================

export function useCreateDeliverable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ rockId, data }: { rockId: string; data: CreateDeliverableInput }) =>
      api.createDeliverable(rockId, data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: rockKeys.detail(variables.rockId) });
      qc.invalidateQueries({ queryKey: rockKeys.all });
      toast.success('Deliverable created');
    },
    onError: (e) =>
      toast.error('Failed to create deliverable', { description: getErrorMessage(e) }),
  });
}

export function useUpdateDeliverable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      rockId: _rockId,
      data,
    }: {
      id: string;
      rockId: string;
      data: Partial<CreateDeliverableInput> & { completed?: boolean };
    }) => api.updateDeliverable(id, data),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: rockKeys.detail(variables.rockId) });
      qc.invalidateQueries({ queryKey: rockKeys.all });
      toast.success('Deliverable updated');
    },
    onError: (e) =>
      toast.error('Failed to update deliverable', { description: getErrorMessage(e) }),
  });
}

export function useDeleteDeliverable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rockId: _rockId }: { id: string; rockId: string }) => api.deleteDeliverable(id),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: rockKeys.detail(variables.rockId) });
      qc.invalidateQueries({ queryKey: rockKeys.all });
      toast.success('Deliverable deleted');
    },
    onError: (e) =>
      toast.error('Failed to delete deliverable', { description: getErrorMessage(e) }),
  });
}

export function useToggleDeliverable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, rockId: _rockId }: { id: string; rockId: string }) => api.toggleDeliverable(id),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: rockKeys.detail(variables.rockId) });
      qc.invalidateQueries({ queryKey: rockKeys.all });
    },
    onError: (e) =>
      toast.error('Failed to toggle deliverable', { description: getErrorMessage(e) }),
  });
}
